/**
 * PIN 碼登入頁面
 * 專為平板觸控操作設計，提供大型數字鍵盤
 */

import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
  getDeviceToken,
  migrateLegacyDeviceToken,
} from '../../utils/deviceToken';

interface PinLoginProps {
  onLoginSuccess: (sessionToken: string, staffName: string) => void;
}

/**
 * PIN 碼登入頁面組件
 */
export function PinLogin({ onLoginSuccess }: PinLoginProps) {
  const [pin, setPin] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 從 localStorage 取得必要的資訊
  const tenantId = localStorage.getItem('tenant_id');

  // 檢查是否有必要的資訊
  useEffect(() => {
    // 遷移舊格式的 device_token（向後相容）
    migrateLegacyDeviceToken();
    // 如果沒有 tenant_id，表示已經離開商店，回到公共區域
    if (!tenantId) {
      toast.error('未找到租戶資訊，已回到公共區域');
      window.location.href = '/tenant-select';
      return;
    }

    const deviceToken = getDeviceToken(tenantId);
    if (!deviceToken) {
      // 如果沒有 device_token，嘗試自動註冊（如果 member 已登入）
      const tenantToken = localStorage.getItem('tenant_token');
      if (tenantToken && tenantId) {
        (async () => {
          const { autoRegisterDevice } = await import(
            '../../utils/deviceToken.js'
          );
          await autoRegisterDevice(tenantId, tenantToken);
          // 重新檢查 device token
          const newDeviceToken = getDeviceToken(tenantId);
          if (!newDeviceToken) {
            setError('裝置註冊失敗，請重新整理頁面或使用 Member 帳號登入');
          }
        })();
      } else {
        setError('請使用 Member 帳號登入以自動註冊裝置');
      }
    }
  }, [tenantId]);

  /**
   * 處理數字按鈕點擊
   */
  const handleNumberClick = (num: string) => {
    if (pin.length < 6) {
      setPin(prev => prev + num);
      setError(null);
    }
  };

  /**
   * 處理清除按鈕
   */
  const handleClear = () => {
    setPin('');
    setError(null);
  };

  /**
   * 處理刪除最後一位數字
   */
  const handleBackspace = () => {
    setPin(prev => prev.slice(0, -1));
    setError(null);
  };

  /**
   * 處理確認登入
   */
  const handleConfirm = async () => {
    if (pin.length < 4) {
      setError('PIN 碼至少需要 4 位數字');
      return;
    }

    const deviceToken = getDeviceToken(tenantId);
    if (!deviceToken) {
      setError('裝置尚未註冊');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const apiBase =
        import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787';

      const response = await fetch(`${apiBase}/api/auth/device/verify-pin`, {
        method: 'POST',
        headers: {
          'X-Tenant-ID': tenantId || '',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          device_token: deviceToken,
          pin_code: pin,
        }),
      });

      const data = await response.json();

      if (data.success) {
        // 登入成功
        toast.success(`歡迎，${data.data.staff_name}！`);

        // 強制清除 tenant_token，確保身份互斥（單一時刻，單一身份）
        localStorage.removeItem('tenant_token');

        // 保存 Staff Session Token
        localStorage.setItem('staff_session_token', data.data.session_token);
        localStorage.setItem('staff_id', data.data.staff_id);
        localStorage.setItem('staff_name', data.data.staff_name);

        // 通知父組件登入成功
        onLoginSuccess(data.data.session_token, data.data.staff_name);
      } else {
        // 登入失敗
        setError(data.error || 'PIN 碼錯誤');
        setPin(''); // 清除 PIN 碼

        // 如果是裝置被鎖定，顯示特殊訊息
        if (data.code === 'DEVICE_LOCKED') {
          toast.error('裝置已被鎖定，請稍後再試');
        }
      }
    } catch (err) {
      console.error('PIN 碼驗證錯誤:', err);
      setError('網路錯誤，請稍後再試');
      setPin('');
    } finally {
      setIsLoading(false);
    }
  };

  // 如果沒有 device_token，顯示註冊提示
  const deviceToken = getDeviceToken(tenantId);
  if (!deviceToken) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="mb-6">
            <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-10 h-10 text-yellow-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              裝置尚未註冊
            </h1>
            <p className="text-gray-600 mb-6">
              此裝置尚未註冊。請使用 Member
              帳號登入以自動註冊裝置，或重新整理頁面。
            </p>
          </div>
          <button
            onClick={() => (window.location.href = '/tenant-select')}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-6 rounded-xl transition-colors text-lg"
          >
            返回選擇商店
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
        {/* 標題 */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-blue-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">員工登入</h1>
          <p className="text-gray-600">請輸入您的 PIN 碼</p>
        </div>

        {/* PIN 碼顯示區域 */}
        <div className="mb-6">
          <div className="flex justify-center gap-3 mb-4">
            {[0, 1, 2, 3, 4, 5].map(index => (
              <div
                key={index}
                className={`w-12 h-12 rounded-lg border-2 flex items-center justify-center text-2xl font-bold transition-all ${
                  index < pin.length
                    ? 'border-blue-600 bg-blue-50 text-blue-600'
                    : 'border-gray-300 bg-gray-50 text-gray-400'
                }`}
              >
                {index < pin.length ? '•' : ''}
              </div>
            ))}
          </div>

          {/* 錯誤訊息 */}
          {error && (
            <div className="text-center">
              <p className="text-red-600 text-sm font-medium">{error}</p>
            </div>
          )}
        </div>

        {/* 數字鍵盤 */}
        <div className="grid grid-cols-3 gap-4 mb-4">
          {/* 數字 1-9 */}
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
            <button
              key={num}
              onClick={() => handleNumberClick(num.toString())}
              disabled={isLoading}
              className="h-16 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 rounded-xl text-2xl font-bold text-gray-900 transition-all disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
            >
              {num}
            </button>
          ))}

          {/* 清除按鈕 */}
          <button
            onClick={handleClear}
            disabled={isLoading || pin.length === 0}
            className="h-16 bg-red-100 hover:bg-red-200 active:bg-red-300 rounded-xl text-lg font-semibold text-red-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
          >
            清除
          </button>

          {/* 數字 0 */}
          <button
            onClick={() => handleNumberClick('0')}
            disabled={isLoading}
            className="h-16 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 rounded-xl text-2xl font-bold text-gray-900 transition-all disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
          >
            0
          </button>

          {/* 刪除按鈕 */}
          <button
            onClick={handleBackspace}
            disabled={isLoading || pin.length === 0}
            className="h-16 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 rounded-xl text-lg font-semibold text-gray-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
          >
            ⌫
          </button>
        </div>

        {/* 確認按鈕 */}
        <button
          onClick={handleConfirm}
          disabled={isLoading || pin.length < 4}
          className="w-full h-16 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-bold text-lg rounded-xl transition-all touch-manipulation"
        >
          {isLoading ? (
            <span className="flex items-center justify-center">
              <svg
                className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              驗證中...
            </span>
          ) : (
            '確認登入'
          )}
        </button>

        {/* 提示訊息 */}
        <p className="text-center text-sm text-gray-500 mt-4">
          PIN 碼長度：4-6 位數字
        </p>
      </div>
    </div>
  );
}
